/**
 * TikTok 发布记录审核态同步：对本地有 product_id（platformSpuName）的记录
 * 并行调 Get Product，按 audit.status 回写（PRE_APPROVED/APPROVED → 已通过）。
 */

import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import {
  listTiktokRefreshablePublishRecords,
  updateTiktokLocalPublishRecordPlatformStatus,
} from '@/tiktok-platform/utils/tiktokLocalPublishRecordStore';
import {
  extractAuditFailedRemark,
  mapPlatformAuditToPublishLabel,
} from '@/tiktok-platform/utils/tiktokPublishRecordStatus';

const DETAIL_CONCURRENCY = 4;

/** @type {Map<string, object>} */
const jobs = new Map();

function makeJobId(shopId) {
  return `tpr-sync-${shopId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function unwrapProductPayload(res) {
  if (!res || typeof res !== 'object') return null;
  if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
    const data = res.data;
    if (data.audit || data.status || data.product_status || data.id || data.product_id) {
      return data;
    }
    if (data.data && typeof data.data === 'object') return data.data;
  }
  if (res.audit || res.status || res.product_status) return res;
  return null;
}

async function mapPool(items, concurrency, mapper) {
  const list = Array.isArray(items) ? items : [];
  const out = new Array(list.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(Math.max(1, concurrency), Math.max(1, list.length)) }, async () => {
    while (cursor < list.length) {
      const i = cursor;
      cursor += 1;
      out[i] = await mapper(list[i], i);
    }
  });
  await Promise.all(workers);
  return out;
}

/**
 * @param {string|number} shopId
 * @param {(productId: string) => Promise<object>} getProductFn
 * @param {{ onProgress?: (p: object) => void, shouldCancel?: () => boolean }} [opts]
 */
export async function syncTiktokPublishRecordStatusesFromPlatform(shopId, getProductFn, opts = {}) {
  const records = listTiktokRefreshablePublishRecords(shopId) || [];
  const total = records.length;
  let processed = 0;
  let imported = 0;
  let failed = 0;
  let firstError = '';

  const report = (extra = {}) => {
    opts.onProgress?.({
      total,
      processed,
      imported,
      failed,
      percent: total > 0 ? Math.min(99, Math.round((processed / total) * 100)) : 100,
      ...extra,
    });
  };

  report({
    message: total
      ? fixedUiTpl('正在同步审核状态 {0}/{1}', 0, total)
      : fixedUi('暂无可同步的发布记录（需有平台商品 ID）'),
  });

  if (!total) {
    return {
      imported: 0,
      failed: 0,
      total: 0,
      processed: 0,
      message: fixedUi('暂无可同步的发布记录（需有平台商品 ID）'),
    };
  }

  await mapPool(records, DETAIL_CONCURRENCY, async (row) => {
    if (opts.shouldCancel?.()) return;
    const productId = String(row.platformSpuName || '').trim();
    if (!productId) {
      processed += 1;
      failed += 1;
      report({ message: fixedUiTpl('正在同步审核状态 {0}/{1}', processed, total) });
      return;
    }
    try {
      const res = await getProductFn(productId);
      if (opts.shouldCancel?.()) return;
      const payload = unwrapProductPayload(res);
      if (!payload) {
        throw new Error(fixedUiTpl('Get Product 无数据 productId={0}', productId));
      }
      const auditStatus = payload?.audit?.status ?? payload?.audit_status ?? null;
      const productStatus = payload?.status ?? payload?.product_status ?? null;
      const label = mapPlatformAuditToPublishLabel(auditStatus, productStatus);
      const remark = label === '已驳回' ? extractAuditFailedRemark(payload) : '';
      await updateTiktokLocalPublishRecordPlatformStatus(shopId, row.id, {
        statusLabel: label,
        remark: remark || undefined,
        platformAuditStatus: auditStatus != null ? String(auditStatus) : null,
        platformProductStatus: productStatus != null ? String(productStatus) : null,
        platformProductRaw: payload,
      });
      imported += 1;
    } catch (err) {
      failed += 1;
      if (!firstError) {
        firstError = String(err?.message || err || fixedUi('Get Product 失败')).trim();
      }
    } finally {
      processed += 1;
      report({ message: fixedUiTpl('正在同步审核状态 {0}/{1}', processed, total) });
    }
  });

  if (opts.shouldCancel?.()) {
    return {
      imported,
      failed,
      total,
      processed,
      cancelled: true,
      message: fixedUi('已终止同步'),
      errorSample: firstError || undefined,
    };
  }

  return {
    imported,
    failed,
    total,
    processed,
    message: imported > 0
      ? fixedUiTpl(
        '已同步审核状态：更新 {0} 条{1}',
        imported,
        failed > 0 ? fixedUiTpl('，失败 {0} 条', failed) : '',
      )
      : (firstError || fixedUi('同步完成，但未能更新任何记录')),
    errorSample: firstError || undefined,
  };
}

export function startTiktokPublishRecordSyncJob(shopId, getProductFn) {
  const id = String(shopId || '').trim();
  if (!id) {
    return Promise.reject(new Error(fixedUi('缺少店铺 ID')));
  }
  const jobId = makeJobId(id);
  const job = {
    jobId,
    shopId: id,
    status: 'running',
    done: false,
    percent: 0,
    imported: 0,
    processed: 0,
    total: 0,
    failed: 0,
    message: fixedUi('正在同步发布记录审核状态…'),
    finishedAt: null,
    cancelRequested: false,
  };
  jobs.set(jobId, job);

  (async () => {
    try {
      const result = await syncTiktokPublishRecordStatusesFromPlatform(
        id,
        (productId) => getProductFn(id, productId),
        {
          shouldCancel: () => Boolean(jobs.get(jobId)?.cancelRequested),
          onProgress: (p) => {
            const cur = jobs.get(jobId);
            if (!cur || cur.done) return;
            Object.assign(cur, {
              percent: p.percent,
              imported: p.imported,
              processed: p.processed,
              total: p.total,
              failed: p.failed,
              message: p.message || cur.message,
            });
          },
        },
      );
      const cur = jobs.get(jobId);
      if (!cur) return;
      const cancelled = Boolean(result.cancelled || cur.cancelRequested);
      Object.assign(cur, {
        status: cancelled ? 'cancelled' : (result.imported > 0 || result.total === 0 ? 'success' : 'failed'),
        done: true,
        percent: 100,
        imported: result.imported,
        processed: result.processed,
        total: result.total,
        failed: result.failed,
        message: result.message,
        error: result.imported === 0 && result.total > 0 ? result.errorSample : undefined,
        finishedAt: new Date().toISOString(),
      });
    } catch (err) {
      const cur = jobs.get(jobId);
      if (!cur) return;
      Object.assign(cur, {
        status: 'failed',
        done: true,
        percent: 100,
        message: String(err?.message || err || fixedUi("同步失败")),
        error: String(err?.message || err || fixedUi("同步失败")),
        finishedAt: new Date().toISOString(),
      });
    }
  })();

  return Promise.resolve({
    jobId,
    status: 'running',
    done: false,
    percent: 0,
    imported: 0,
    processed: 0,
    total: 0,
    message: job.message,
  });
}

export function getTiktokPublishRecordSyncJob(shopId, jobId) {
  const job = jobs.get(String(jobId || ''));
  if (!job || String(job.shopId) !== String(shopId)) {
    return Promise.resolve({
      jobId,
      status: 'failed',
      done: true,
      percent: 100,
      message: fixedUi('同步任务不存在或已过期'),
      error: fixedUi('同步任务不存在或已过期'),
    });
  }
  return Promise.resolve({ ...job });
}

export function cancelTiktokPublishRecordSyncJob(shopId, jobId) {
  const job = jobs.get(String(jobId || ''));
  if (!job || String(job.shopId) !== String(shopId)) {
    return Promise.resolve({ ok: true, stub: true });
  }
  job.cancelRequested = true;
  if (!job.done) {
    job.status = 'cancelled';
    job.message = fixedUi("正在终止…");
  }
  return Promise.resolve({ ok: true });
}

/** 测试辅助：清理内存任务 */
export function __resetTiktokPublishRecordSyncJobsForTest() {
  jobs.clear();
}

export { sleep };
